import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

// LDF: Hinweis „Als App installieren“ auf dem Handy – nur im Browser (nicht, wenn schon als App geöffnet), wegklickbar.
const KEY = "ldf.install-hint-dismissed";

type InstallPromptEvent = Event & { prompt: () => Promise<void> };

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const InstallHint = ({ docked = true }: { docked?: boolean }) => {
  const [visible, setVisible] = useState(false);
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  // iPadOS meldet sich als „Macintosh“ – erkennbar am Touchscreen
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(KEY) === "1";
    } catch {
      // Speicher nicht verfügbar
    }
    if (dismissed || isStandalone()) return;
    // Am Computer ohne Touchscreen nie anzeigen
    if (!isIos && !("ontouchstart" in window)) return;
    if (isIos) setVisible(true);
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as InstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [isIos]);

  const close = () => {
    setVisible(false);
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      // Speicher nicht verfügbar
    }
  };

  if (!visible) return null;
  return (
    <div
      className={`fixed z-40 flex items-start gap-3 rounded-xl border border-border bg-card p-3 text-sm shadow-lg ${docked ? "left-3 right-3" : "left-4 max-w-sm"}`}
      style={{ bottom: docked ? "calc(4.5rem + env(safe-area-inset-bottom))" : "calc(1rem + env(safe-area-inset-bottom))" }}
      role="dialog"
      aria-label="CRM als App installieren"
    >
      <img src="./brand/apple-touch-icon.png" alt="" className="h-10 w-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">CRM als App auf den Home-Bildschirm</p>
        {prompt ? (
          <button type="button" onClick={() => prompt.prompt().then(close)} className="mt-1 inline-flex items-center gap-1 font-medium text-primary cursor-pointer">
            <Download className="h-4 w-4" /> Jetzt installieren
          </button>
        ) : (
          <p className="text-muted-foreground">
            Auf <Share className="inline h-4 w-4 align-text-bottom" /> <strong>Teilen</strong> tippen, dann <strong>„Zum Home-Bildschirm“</strong>. Danach öffnet
            sich das CRM ohne Browserleisten.
          </p>
        )}
      </div>
      <button type="button" onClick={close} aria-label="Hinweis schließen" className="-m-1 p-1 text-muted-foreground cursor-pointer">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
};
